import { Alert, Button, Group, Modal, Radio, Stack, Text, TextInput } from "@mantine/core";
import { ActionCard } from "../../../../components/ActionCard";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { useCallback, useMemo } from "react";
import { z } from "zod";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { IconAlertTriangle } from "@tabler/icons-react";
import { FIXED_PRICES, FIXED_PRICES_PRODUCT_IDS } from "@project/common";
import { useTRPC } from "../../../../trpc";
import { useMapkaNotification } from "../../../../hooks/notifications";
import type { FixedPricesProductId } from "@project/common";

interface FormValuesCancelSubscription {
  [key: string]: string | undefined;
  comment?: string;
  feedback: "customer_service" | "low_quality" | "missing_features" | "other" | "switched_service" | "too_complex" | "too_expensive" | "unused";
}

const isFixedPriceProductId = (nickname: string): nickname is FixedPricesProductId =>
  FIXED_PRICES_PRODUCT_IDS.includes(nickname as FixedPricesProductId);

export const CancelSubscriptionCard = () => {
  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mapkaNotification = useMapkaNotification();

  const { data: subscriptions = [] } = useQuery(trpc.getCurrentSubscriptions.queryOptions());
  const { mutate: cancelSubscription, isPending } = useMutation(trpc.cancelSubscription.mutationOptions());

  const [opened, { open, close }] = useDisclosure(false);

  const validate = useMemo(() => zod4Resolver(z.object({
    comment: z.string().min(3, {
      message: intl.formatMessage({ defaultMessage: "Comment should have at least 3 characters", id: "error.commentTooShort3Chars" }),
    }),
  })), [intl]);

  const { onSubmit, getInputProps, reset } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      feedback: "other" as const,
      comment: undefined,
    },
    validate,
  });

  const handleClose = useCallback(() => {
    reset();
    close();
  }, [reset, close]);

  const handleSubmit = useCallback((form: FormValuesCancelSubscription) => {
    cancelSubscription(form, {
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.getCurrentSubscriptions.queryFilter());
        void queryClient.invalidateQueries(trpc.getCurrentUser.queryFilter());
        mapkaNotification({
          title: intl.formatMessage({ defaultMessage: "Subscription canceled", id: "notification.subscriptionCanceled" }),
          message: intl.formatMessage({ defaultMessage: "Your account has been moved to the free tier.", id: "notification.subscriptionCanceledMessage" }),
          variant: "success",
        });
        handleClose();
      },
    });
  }, [cancelSubscription, queryClient, trpc, mapkaNotification, intl, handleClose]);

  const activeSubscriptions = subscriptions.filter(({ status }) => status === "active");

  // Nothing to cancel on a free-tier account
  if (activeSubscriptions.length === 0) {
    return null;
  }

  const addonNames = activeSubscriptions
    .flatMap(({ prices }) => prices)
    .map(({ nickname }) => nickname)
    .filter(isFixedPriceProductId)
    .map(productId => FIXED_PRICES[productId].name);

  return (
    <>
      <ActionCard
        title={intl.formatMessage({ defaultMessage: "Cancel subscription", id: "title.cancelSubscription" })}
        subTitle={intl.formatMessage({
          defaultMessage: "Stop usage-based billing and cancel every add-on. Your account moves to the free tier.",
          id: "text.cancelSubscriptionDescription",
        })}
      >
        <Button variant="danger" onClick={open}>
          <FormattedMessage defaultMessage="Cancel subscription" id="button.cancelSubscription" />
        </Button>
      </ActionCard>

      <Modal
        size="lg"
        opened={opened}
        onClose={handleClose}
        title={intl.formatMessage({ defaultMessage: "Cancel subscription", id: "title.cancelSubscription" })}
      >
        <form onSubmit={onSubmit(handleSubmit)}>
          <Stack gap="xs">
            <Text size="sm">
              <FormattedMessage
                defaultMessage="Are you sure you want to cancel? Cancellation takes effect immediately."
                id="text.cancelSubscriptionConfirm"
              />
            </Text>

            <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
              <Stack gap="xs">
                <Text size="sm">
                  <FormattedMessage
                    defaultMessage="Usage above the free limits will stop working, and a final invoice will cover the usage accrued so far."
                    id="text.cancelSubscriptionConsequences"
                  />
                </Text>
                {addonNames.length > 0 && (
                  <Text size="sm">
                    <FormattedMessage
                      defaultMessage="These add-ons will be cancelled as well: {addons}."
                      id="text.cancelSubscriptionAddons"
                      values={{ addons: addonNames.join(", ") }}
                    />
                  </Text>
                )}
              </Stack>
            </Alert>

            <Radio.Group
              label={intl.formatMessage({ defaultMessage: "Select reason for your decision", id: "label.selectReasonForYourDecision" })}
              required
              {...getInputProps("feedback")}
            >
              <Stack mt="xs">
                <Radio value="customer_service" label={intl.formatMessage({ defaultMessage: "Customer service", id: "label.customerService" })} />
                <Radio value="low_quality" label={intl.formatMessage({ defaultMessage: "Low quality", id: "label.lowQuality" })} />
                <Radio value="missing_features" label={intl.formatMessage({ defaultMessage: "Missing features", id: "label.missingFeatures" })} />
                <Radio value="switched_service" label={intl.formatMessage({ defaultMessage: "Switched service", id: "label.switchedService" })} />
                <Radio value="too_complex" label={intl.formatMessage({ defaultMessage: "Too complex", id: "label.tooComplex" })} />
                <Radio value="too_expensive" label={intl.formatMessage({ defaultMessage: "Too expensive", id: "label.tooExpensive" })} />
                <Radio value="unused" label={intl.formatMessage({ defaultMessage: "Unused", id: "label.unused" })} />
                <Radio value="other" label={intl.formatMessage({ defaultMessage: "Other", id: "label.other" })} />
              </Stack>
            </Radio.Group>

            <TextInput
              label={intl.formatMessage({ defaultMessage: "Comment", id: "label.comment" })}
              {...getInputProps("comment")}
            />

            <Group gap="xs" justify="flex-end" pt="lg">
              <Button variant="secondary" type="reset" loading={isPending} onClick={handleClose}>
                <FormattedMessage defaultMessage="Keep subscription" id="button.keepSubscription" />
              </Button>

              <Button variant="danger" type="submit" loading={isPending}>
                <FormattedMessage defaultMessage="Cancel subscription" id="button.cancelSubscription" />
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};
